import { createEffect, onCleanup, untrack, Show } from "solid-js";
import { useTranslation } from "../hooks/useTranslation";
import Text from "./Text";
import { EmptyState } from "./EmptyState";
import type { BotEngineMetricBucketDto } from "../api/generated/schemas";
import {
    Chart,
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Filler,
    Tooltip,
} from "chart.js";
import "chartjs-adapter-moment";

Chart.register(TimeScale, LinearScale, PointElement, LineElement, LineController, Filler, Tooltip);

interface Props {
    titleKey: string;
    buckets: BotEngineMetricBucketDto[];
    loading: boolean;
    value: (b: BotEngineMetricBucketDto) => number | null | undefined;
    color: string;
    unit?: string;
    decimals?: number;
}

export default function MetricHistoryChart(props: Props) {
    const { t } = useTranslation();

    let canvas: HTMLCanvasElement | undefined;
    let chart: Chart | undefined;

    function data() {
        return props.buckets.map(b => ({ x: new Date(b.t).toISOString(), y: props.value(b) ?? null }));
    }

    createEffect(() => {
        const buckets = props.buckets;
        untrack(() => { if (canvas && canvas.isConnected) renderChart(); });
        void buckets;
    });

    function mountCanvas(el: HTMLCanvasElement) {
        canvas = el;
        queueMicrotask(() => { if (el.isConnected) renderChart(); });
    }

    function renderChart() {
        if (!canvas) return;
        if (chart) { chart.destroy(); chart = undefined; }
        const points = data();
        if (points.length === 0) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const decimals = props.decimals ?? 0;
        const unit = props.unit ?? "";

        chart = new Chart(ctx, {
            type: "line",
            data: {
                datasets: [{
                    label: t(props.titleKey),
                    data: points as any,
                    borderColor: props.color,
                    backgroundColor: `${props.color}22`,
                    borderWidth: 2,
                    fill: true,
                    tension: 0.2,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    spanGaps: false,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                interaction: { mode: "nearest", intersect: false },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: "rgba(30, 30, 30, 0.95)",
                        titleColor: "#ffffff",
                        bodyColor: "#e0e0e0",
                        borderColor: "#444444",
                        borderWidth: 1,
                        callbacks: {
                            label: (item) => {
                                const v = item.parsed.y;
                                if (v == null) return t("BOT_HISTORY_NO_DATA");
                                return `${v.toFixed(decimals)}${unit}`;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        type: "time",
                        time: { tooltipFormat: "DD.MM.YYYY, HH:mm" },
                        ticks: { color: "#999999", maxRotation: 0, autoSkipPadding: 16 },
                        grid: { color: "rgba(255, 255, 255, 0.05)" },
                    },
                    y: {
                        beginAtZero: true,
                        ticks: { color: "#999999", callback: (v) => `${v}${unit}` },
                        grid: { color: "rgba(255, 255, 255, 0.05)" },
                    },
                },
            },
        });
    }

    onCleanup(() => { if (chart) chart.destroy(); });

    return (
        <div class="fcard v" style={{ gap: "var(--spacing-sm)" }}>
            <h3 style={{ margin: 0, "font-size": "var(--font-size-lg)" }}><Text val={props.titleKey} /></h3>

            <Show
                when={!props.loading}
                fallback={
                    <div class="hcenter" style={{ "justify-content": "center", height: "12rem", color: "var(--dark-text-muted)" }}>
                        <i class="fa-solid fa-spinner fa-spin fa-2x" />
                    </div>
                }
            >
                <Show
                    when={props.buckets.length > 0}
                    fallback={<EmptyState icon="fa-chart-line" title={t("BOT_HISTORY_EMPTY")} />}
                >
                    <div style={{ position: "relative", height: "12rem", width: "100%" }}>
                        <canvas ref={mountCanvas} />
                    </div>
                </Show>
            </Show>
        </div>
    );
}
